import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import {
  ArrowLeft, FileText, Building, Truck, Warehouse, Calendar,
  DollarSign, CreditCard, Smartphone, Wallet, Package, Hash,
  User, Edit, Printer, Download, Mail, AlertCircle, CheckCircle,
  Clock, XCircle, Loader2, Eye, Plus, Layers, Receipt
} from "lucide-react";
import "./PurchaseInvoiceView.css";

const url = process.env.REACT_APP_BACKEND_URL;

const PurchaseInvoiceView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency } = useCurrency();

  const [invoice, setInvoice] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [supplierPayments, setSupplierPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  const isCompanyAdmin = userType === 'company' && currentUser?.role === 'admin';

  useEffect(() => {
    fetchInvoiceData();
    fetchSupplierPayments();
  }, [id]);

  const fetchInvoiceData = async () => {
    try {
      setLoading(true);

      const invoiceResponse = await axios.get(`${url}/api/purchase/invoice/${id}`);

      if (invoiceResponse.data.success) {
        setInvoice(invoiceResponse.data.data);

        const transactionsResponse = await axios.get(
          `${url}/api/purchase/transactions/by-invoice/${id}`
        );

        if (transactionsResponse.data.success) {
          setTransactions(transactionsResponse.data.data);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load invoice details");
    } finally {
      setLoading(false);
    }
  };

  const fetchSupplierPayments = async () => {
    try {
      setLoadingPayments(true);

      const response = await axios.get(`${url}/api/supplierpayment/by-invoice/${id}/details`);

      if (response.data.success) {
        setSupplierPayments(response.data.data);
      }
    } catch (error) {
      // Silent fail
    } finally {
      setLoadingPayments(false);
    }
  };

  // Show ALL supplier payments – no filtering
  const allSupplierPayments = supplierPayments;

  // Total from supplier payments (for display breakdown)
  const totalSupplierPayments = allSupplierPayments.reduce((sum, p) => sum + (p.total_paying_now || 0), 0);

  // Purchase-form payment is stored on the invoice; later payments are SupplierPayment documents.
  const totalPaidFromPurchase = Number(invoice?.total_amount_paid || 0);
  const totalPaidOverall = totalPaidFromPurchase + totalSupplierPayments;
  const grandTotal = Number(invoice?.grand_total || 0);
  const balance = Math.max(grandTotal - totalPaidOverall, 0);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return "Invalid date";
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { icon: Clock, bg: '#fff3cd', color: '#856404', label: 'Pending' },
      process: { icon: Clock, bg: '#cce5ff', color: '#004085', label: 'Process' },
      completed: { icon: CheckCircle, bg: '#d4edda', color: '#155724', label: 'Completed' },
      cancelled: { icon: XCircle, bg: '#f8d7da', color: '#721c24', label: 'Cancelled' },
      returned: { icon: AlertCircle, bg: '#e2d5f1', color: '#4a1c6c', label: 'Returned' }
    };

    const config = statusConfig[status] || statusConfig.process;
    const Icon = config.icon;

    return (
      <span className={`status-badge status-${status}`} style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: 600,
        backgroundColor: config.bg,
        color: config.color
      }}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: '#fff3cd', color: '#856404', label: 'Pending' },
      paid: { bg: '#d4edda', color: '#155724', label: 'Paid' },
      partial: { bg: '#cce5ff', color: '#004085', label: 'Partial' },
      notpaid: { bg: '#f8d7da', color: '#721c24', label: 'Not Paid' },
      overdue: { bg: '#f8d7da', color: '#721c24', label: 'Overdue' }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`payment-badge payment-${status}`} style={{
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: 600,
        backgroundColor: config.bg,
        color: config.color
      }}>
        {config.label}
      </span>
    );
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'cash': return <CreditCard size={16} />;
      case 'evc': return <Smartphone size={16} />;
      case 'edahab': return <Wallet size={16} />;
      case 'bank_transfer': return <Building size={16} />;
      default: return <DollarSign size={16} />;
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: 'Cash',
      evc: 'EVC',
      edahab: 'eDahab',
      bank_transfer: 'Bank Transfer',
      credit: 'Credit',
      check: 'Check',
      online: 'Online'
    };
    return labels[method] || method;
  };

  const getProductInfo = (transaction) => {
    if (transaction.product_id) {
      if (typeof transaction.product_id === 'object') {
        return {
          name: transaction.product_id.product_name || "Unknown Product",
          code: transaction.product_id.product_code ||
            transaction.product_id.product_no ||
            "N/A",
          sku: transaction.product_id.sku || "N/A"
        };
      }
    }
    return { name: "Unknown Product", code: "N/A", sku: "N/A" };
  };

  const getWarehouseInfo = (transaction) => {
    if (transaction.warehouse_id) {
      if (typeof transaction.warehouse_id === 'object') {
        return {
          name: transaction.warehouse_id.warehouse_name || "Unknown Warehouse",
          code: transaction.warehouse_id.warehouse_code || "N/A"
        };
      }
    }
    return { name: "Unknown Warehouse", code: "N/A" };
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    try {
      toast.info("PDF download feature coming soon");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleEmailInvoice = async () => {
    try {
      toast.info("Email feature coming soon");
    } catch (error) {
      toast.error("Failed to send email");
    }
  };

  const handleEdit = () => {
    if (transactions && transactions.length > 0) {
      navigate(`/purchase/edit/${transactions[0]._id}`);
    }
    else if (invoice?.purchase_transactions && invoice.purchase_transactions.length > 0) {
      navigate(`/purchase/edit/${invoice.purchase_transactions[0]}`);
    }
    else {
      toast.info("This invoice has no editable transactions");
    }
  };

  const handleBack = () => {
    navigate("/purchase/invoices");
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="animate-spin" size={48} />
        <p>Loading invoice details...</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="error-container">
        <AlertCircle size={48} color="#f5222d" />
        <h3>Invoice Not Found</h3>
        <p>The requested invoice could not be found.</p>
        <button onClick={handleBack} className="back-button">
          <ArrowLeft size={16} /> Back to Invoices
        </button>
      </div>
    );
  }

  return (
    <div className="invoice-view-container">
      {/* Header */}
      <div className="view-header">
        <button onClick={handleBack} className="back-btn">
          <ArrowLeft size={20} />
          <span>Back to Invoices</span>
        </button>

        <div className="header-actions">
          <button onClick={handlePrint} className="action-btn" title="Print">
            <Printer size={18} />
          </button>
          <button onClick={handleDownloadPDF} className="action-btn" title="Download PDF">
            <Download size={18} />
          </button>
          <button onClick={handleEmailInvoice} className="action-btn" title="Email">
            <Mail size={18} />
          </button>
          <button onClick={handleEdit} className="edit-btn">
            <Edit size={18} />
            <span>Edit Invoice</span>
          </button>
        </div>
      </div>

      {/* Invoice Header Card */}
      <div className="invoice-header-card">
        <div className="invoice-title">
          <div className="title-left">
            <Receipt size={32} color="#1890ff" />
            <div>
              <h1>Purchase Invoice</h1>
              <div className="invoice-refs">
                <span className="ref-id">#{invoice.reference_id || `INV-${invoice.invoice_id}`}</span>
                <span className="invoice-no">{invoice.invoice_no}</span>
              </div>
            </div>
          </div>
          <div className="title-right">
            {getStatusBadge(invoice.status)}
            {getPaymentStatusBadge(invoice.payment_status)}
          </div>
        </div>

        <div className="invoice-meta-grid">
          <div className="meta-item">
            <Building size={18} className="meta-icon" />
            <div className="meta-content">
              <span className="meta-label">Company</span>
              <span className="meta-value">
                {invoice.company_id?.company_name || "N/A"}
                {invoice.company_id?.company_code && (
                  <span className="meta-code">({invoice.company_id.company_code})</span>
                )}
              </span>
            </div>
          </div>

          <div className="meta-item">
            <Truck size={18} className="meta-icon" />
            <div className="meta-content">
              <span className="meta-label">Supplier</span>
              <span className="meta-value">
                {invoice.supplier_id?.supplier_name || "N/A"}
                {invoice.supplier_id?.supplier_code && (
                  <span className="meta-code">({invoice.supplier_id.supplier_code})</span>
                )}
              </span>
            </div>
          </div>

          <div className="meta-item">
            <Warehouse size={18} className="meta-icon" />
            <div className="meta-content">
              <span className="meta-label">Warehouse</span>
              <span className="meta-value">
                {invoice.warehouse_id?.warehouse_name || "N/A"}
                {invoice.warehouse_id?.warehouse_code && (
                  <span className="meta-code">({invoice.warehouse_id.warehouse_code})</span>
                )}
              </span>
            </div>
          </div>

          <div className="meta-item">
            <Calendar size={18} className="meta-icon" />
            <div className="meta-content">
              <span className="meta-label">Invoice Date</span>
              <span className="meta-value">{formatDate(invoice.invoice_date)}</span>
            </div>
          </div>

          <div className="meta-item">
            <Calendar size={18} className="meta-icon" />
            <div className="meta-content">
              <span className="meta-label">Due Date</span>
              <span className="meta-value">{formatDate(invoice.due_date) || "Not set"}</span>
            </div>
          </div>

          <div className="meta-item">
            <User size={18} className="meta-icon" />
            <div className="meta-content">
              <span className="meta-label">Created By</span>
              <span className="meta-value">
                {invoice.created_by?.Usercomp_sure_name ||
                  invoice.created_by?.name ||
                  "System"}
                <span className="meta-date">{formatDate(invoice.created_at)}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="financial-summary">
          <div className="summary-item">
            <span className="summary-label">Sub Total</span>
            <span className="summary-value">{formatCurrency(invoice.sub_total)}</span>
          </div>

          {invoice.total_discount > 0 && (
            <div className="summary-item discount">
              <span className="summary-label">Discount</span>
              <span className="summary-value">-{formatCurrency(invoice.total_discount)}</span>
            </div>
          )}

          {invoice.total_tax > 0 && (
            <div className="summary-item">
              <span className="summary-label">Tax</span>
              <span className="summary-value">{formatCurrency(invoice.total_tax)}</span>
            </div>
          )}

          {invoice.shipping_cost > 0 && (
            <div className="summary-item">
              <span className="summary-label">Shipping</span>
              <span className="summary-value">{formatCurrency(invoice.shipping_cost)}</span>
            </div>
          )}

          {invoice.other_charges > 0 && (
            <div className="summary-item">
              <span className="summary-label">Other Charges</span>
              <span className="summary-value">{formatCurrency(invoice.other_charges)}</span>
            </div>
          )}

          <div className="summary-item grand-total">
            <span className="summary-label">Grand Total</span>
            <span className="summary-value">{formatCurrency(invoice.grand_total)}</span>
          </div>

          <div className="summary-item balance">
            <span className="summary-label">Balance</span>
            <span className="summary-value" style={{ color: balance > 0 ? '#ff4d4f' : balance < 0 ? '#ff4d4f' : '#52c41a' }}>
              {formatCurrency(balance)}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button
          className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          <FileText size={16} />
          Invoice Details
        </button>
        <button
          className={`tab-btn ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          <Layers size={16} />
          Transactions ({transactions.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          <DollarSign size={16} />
          Payment Details ({allSupplierPayments.length})
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Invoice Details Tab */}
        {activeTab === 'details' && (
          <div className="details-tab">
            <div className="info-section">
              <h3>Additional Information</h3>
              <div className="info-grid">
                <div className="info-row">
                  <span className="info-label">Invoice ID:</span>
                  <span className="info-value">{invoice.invoice_id || "N/A"}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Reference ID:</span>
                  <span className="info-value">{invoice.reference_id || "N/A"}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Invoice Number:</span>
                  <span className="info-value">{invoice.invoice_no}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Is Return:</span>
                  <span className="info-value">
                    {invoice.is_return ? (
                      <span style={{ color: '#ff4d4f', fontWeight: 600 }}>Yes</span>
                    ) : (
                      <span style={{ color: '#52c41a' }}>No</span>
                    )}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Transaction Count:</span>
                  <span className="info-value">{transactions.length}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Supplier Payments:</span>
                  <span className="info-value">{allSupplierPayments.length}</span>
                </div>
                <div className="info-row full-width">
                  <span className="info-label">Notes:</span>
                  <span className="info-value">{invoice.notes || "No notes provided"}</span>
                </div>
              </div>
            </div>

            {invoice.excel_upload_id && (
              <div className="excel-info">
                <FileText size={16} />
                <span>Imported from Excel (Upload ID: {invoice.excel_upload_id})</span>
              </div>
            )}
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="transactions-tab">
            <div className="transactions-header">
              <h3>Purchase Transactions</h3>
            </div>

            {transactions.length === 0 ? (
              <div className="no-transactions">
                <Package size={48} color="#ccc" />
                <p>No transactions found for this invoice</p>
              </div>
            ) : (
              <div className="transactions-table-container">
                <table className="transactions-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Batch</th>
                      <th>Product</th>
                      <th>Warehouse</th>
                      <th>Quantity</th>
                      <th>Unit Cost</th>
                      <th>Discount</th>
                      <th>Total</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((transaction) => {
                      const productInfo = getProductInfo(transaction);
                      const warehouseInfo = getWarehouseInfo(transaction);
                      const isReturn = transaction.is_return;

                      return (
                        <tr key={transaction._id} className={isReturn ? 'return-row' : ''}>
                          <td>
                            <div className="transaction-ref">
                              <span className="ref-id">{transaction.reference_id}</span>
                              <span className="invoice-no">{transaction.invoice_no}</span>
                            </div>
                          </td>
                          <td>
                            <span className="batch-number">
                              {transaction.batch_number || '-'}
                            </span>
                          </td>
                          <td>
                            <div className="product-info">
                              <Package size={14} />
                              <div>
                                <div className="product-name">{productInfo.name}</div>
                                <div className="product-code">{productInfo.code}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="warehouse-info">
                              <Warehouse size={14} />
                              <span>{warehouseInfo.name}</span>
                            </div>
                          </td>
                          <td className={`quantity ${isReturn ? 'return' : ''}`}>
                            {isReturn ? '-' : ''}{Math.abs(transaction.quantity)}
                          </td>
                          <td>{formatCurrency(transaction.unit_cost)}</td>
                          <td>
                            {Number(transaction.discount_amount || 0) > 0 ? (
                              <span title={transaction.discount_percent > 0 ? `${transaction.discount_percent}% discount` : "Line discount"}>
                                {formatCurrency(transaction.discount_amount)}
                              </span>
                            ) : transaction.discount_percent > 0 ? (
                              <span>{transaction.discount_percent}%</span>
                            ) : transaction.discount_flat > 0 ? (
                              formatCurrency(transaction.discount_flat)
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="total">{formatCurrency(transaction.total_cost)}</td>
                          <td>{getStatusBadge(transaction.status)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="6" className="footer-label">Total</td>
                      <td className="footer-total">{formatCurrency(invoice.grand_total)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Payments Tab – now showing all supplier payments */}
        {activeTab === 'payments' && (
          <div className="payments-tab">
            <div className="payment-summary">
              <div className="payment-summary-card">
                <span className="label">Total Amount</span>
                <span className="amount">{formatCurrency(grandTotal)}</span>
              </div>
              <div className="payment-summary-card paid">
                <span className="label">Paid Amount</span>
                <span className="amount">{formatCurrency(totalPaidOverall)}</span>
                <small style={{ display: 'block', fontSize: '11px', color: '#666' }}>
                  Purchase: {formatCurrency(totalPaidFromPurchase)} | Supplier: {formatCurrency(totalSupplierPayments)}
                </small>
              </div>
              <div className="payment-summary-card balance">
                <span className="label">Balance Due</span>
                <span className="amount" style={{ color: balance > 0 ? '#ff4d4f' : balance < 0 ? '#ff4d4f' : '#52c41a' }}>
                  {formatCurrency(balance)}
                </span>
                {balance < 0 && (
                  <small style={{ display: 'block', fontSize: '11px', color: '#ff4d4f' }}>
                    Overpaid by {formatCurrency(Math.abs(balance))}
                  </small>
                )}
              </div>
            </div>

            {/* Purchase Form Payments */}
            <div className="payment-details">
              <h3>Purchase Form Payments</h3>
              {invoice.payment_details?.length > 0 ? (
                <table className="payment-table">
                  <thead>
                    <tr>
                      <th>Method</th>
                      <th>Amount</th>
                      <th>Reference</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.payment_details.map((payment, index) => (
                      <tr key={`purchase-${index}`}>
                        <td>
                          <div className="payment-method-cell">
                            {getPaymentMethodIcon(payment.method)}
                            <span>{getPaymentMethodLabel(payment.method)}</span>
                          </div>
                        </td>
                        <td className="payment-amount">{formatCurrency(payment.amount)}</td>
                        <td>{payment.reference || '-'}</td>
                        <td>{formatDate(payment.date)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3" className="footer-label">Total Purchase Form Payments</td>
                      <td className="footer-total">{formatCurrency(totalPaidFromPurchase)}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <p className="no-payments">No purchase form payments</p>
              )}
            </div>

            {/* Supplier Payments */}
            <div className="payment-details">
              <h3>Supplier Payments {loadingPayments && <Loader2 className="animate-spin" size={14} />}</h3>
              {allSupplierPayments.length > 0 ? (
                <table className="payment-table">
                  <thead>
                    <tr>
                      <th>Payment No</th>
                      <th>Method</th>
                      <th>Amount</th>
                      <th>Reference</th>
                      <th>Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allSupplierPayments.map((payment) => (
                      payment.payment_methods.map((method, idx) => (
                        <tr key={`${payment._id}-${idx}`}>
                          {idx === 0 && (
                            <td rowSpan={payment.payment_methods.length}>
                              <strong>{payment.payment_no}</strong>
                            </td>
                          )}
                          <td>
                            <div className="payment-method-cell">
                              {getPaymentMethodIcon(method.method)}
                              <span>{getPaymentMethodLabel(method.method)}</span>
                            </div>
                          </td>
                          <td className="payment-amount">{formatCurrency(method.amount)}</td>
                          <td>{method.reference_no || '-'}</td>
                          <td>{formatDate(payment.payment_date)}</td>
                          {idx === 0 && (
                            <td rowSpan={payment.payment_methods.length}>
                              {getPaymentStatusBadge(payment.payment_status)}
                            </td>
                          )}
                        </tr>
                      ))
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="5" className="footer-label">Total Supplier Payments</td>
                      <td className="footer-total">{formatCurrency(totalSupplierPayments)}</td>
                    </tr>
                    <tr>
                      <td colSpan="5" className="footer-label">Overall Total Paid</td>
                      <td className="footer-total" style={{ fontWeight: 700, color: '#1890ff' }}>
                        {formatCurrency(totalPaidOverall)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <p className="no-payments">No supplier payments found</p>
              )}
            </div>

            {allSupplierPayments.length > 0 && invoice?.total_amount_paid === 0 && (
              <div className="info-message" style={{
                marginTop: '20px',
                padding: '10px',
                background: '#f6ffed',
                borderLeft: '4px solid #52c41a',
                borderRadius: '4px'
              }}>
                <p style={{ margin: 0, fontSize: '13px' }}>
                  <strong>Note:</strong> This invoice was paid entirely through supplier payments. No payment was made during the purchase.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseInvoiceView;